import { arrayMove } from "@dnd-kit/sortable";
import {
    RuleConditionAmount,
    RuleConditionDate,
    RuleConditionType,
    TransactionRule,
    TransactionRuleCondition,
    TransactionRuleGroup,
} from "../../../../common/types/transactionRule";

interface GetNewOrderForRuleReorderParams {
    sourceGroup: TransactionRuleGroup;
    targetGroup: TransactionRuleGroup;
    sourceRuleIndex: number;
    targetRuleIndex: number;
}

export const getNewOrderForRuleReorder = ({
    sourceGroup,
    targetGroup,
    sourceRuleIndex,
    targetRuleIndex,
}: GetNewOrderForRuleReorderParams): number => {
    let result = 0;

    // the rule has been moved inside the same group
    if (sourceGroup.id === targetGroup.id) {
        // Move within the same group
        const updatedRules = arrayMove(
            sourceGroup.rules ?? [],
            sourceRuleIndex,
            targetRuleIndex,
        );

        result =
            ((updatedRules[targetRuleIndex - 1]?.order ?? 0) +
                (updatedRules[targetRuleIndex + 1]?.order ??
                    updatedRules.length + 1)) /
            2;
    } else {
        // Moved to a different group
        const movedRule = sourceGroup.rules?.[sourceRuleIndex];
        if (!movedRule) {
            throw new Error("Moved rule not found");
        }
        const newTargetRules = [
            ...(targetGroup.rules?.slice(0, targetRuleIndex) ?? []),
            ...(movedRule ? [movedRule] : []),
            ...(targetGroup.rules?.slice(targetRuleIndex) ?? []),
        ];

        result =
            ((newTargetRules[targetRuleIndex - 1]?.order ?? 0) +
                (newTargetRules[targetRuleIndex + 1]?.order ??
                    newTargetRules.length + 1)) /
            2;
    }

    return result;
};

const areArraysEqual = <T>(arr1: T[], arr2: T[]): boolean => {
    const set1 = new Set(arr1);
    const set2 = new Set(arr2);

    if (set1.size !== set2.size) {
        return false;
    }

    for (const item of set1) {
        if (!set2.has(item)) {
            return false;
        }
    }

    return true;
};

const isSubset = <T>(subsetArr: T[], mainArr: T[]): boolean => {
    const subsetSet = new Set(subsetArr);
    const mainSet = new Set(mainArr);

    for (const item of subsetSet) {
        if (!mainSet.has(item)) {
            return false;
        }
    }

    return true;
};

export const isConditionASubset = (
    condition: TransactionRuleCondition,
    ruleToCheck: TransactionRule,
) => {
    const conditionsToCheck = ruleToCheck.conditions.filter(
        (c) => c.conditionType === condition.conditionType,
    );
    if (!conditionsToCheck.length) {
        return true;
    }

    if (
        [RuleConditionType.CATEGORY, RuleConditionType.COUNTERPARTY].includes(
            condition.conditionType,
        )
    ) {
        return conditionsToCheck
            .map((conditionToCheck) => conditionToCheck.value as string)
            .includes(condition.value as string);
    } else if (
        [
            RuleConditionType.ENTITY,
            RuleConditionType.FINANCIAL_ACCOUNTS,
            RuleConditionType.DESCRIPTION_IS,
            RuleConditionType.DESCRIPTION_INCLUDES,
        ].includes(condition.conditionType)
    ) {
        // this is not 100% true, but we might decide in the future if we want to allow multiple description conditions
        // it makes sense to allow multiple description conditions, but it is very hard then to evaluate if a rule is a subset of another rule
        // so for now we will go with an optimistic approach
        return conditionsToCheck.every((conditionToCheck) => {
            const arrayValue = Array.isArray(condition.value)
                ? condition.value
                : [condition.value];

            return isSubset(
                arrayValue as string[],
                conditionToCheck.value as string[],
            );
        });
    }
    // there can be multiple amount filters in the past, from now on only single one is allowed,
    // but the current condition must be a subset of every amount filter
    else if (condition.conditionType === RuleConditionType.AMOUNT) {
        return conditionsToCheck.every((conditionToCheck) => {
            const subset = condition.value as RuleConditionAmount;
            const superset = conditionToCheck.value as RuleConditionAmount;

            const minCheck =
                superset.min === undefined ||
                (subset.min !== undefined && superset.min <= subset.min);

            const maxCheck =
                superset.max === undefined ||
                (subset.max !== undefined && superset.max >= subset.max);

            return minCheck && maxCheck;
        });
    } else if (condition.conditionType === RuleConditionType.DATE) {
        return conditionsToCheck.every((conditionToCheck) => {
            const subset = condition.value as RuleConditionDate;
            const superset = conditionToCheck.value as RuleConditionDate;

            const minCheck =
                superset.after === undefined ||
                (subset.after !== undefined &&
                    new Date(superset.after).getTime() <=
                        new Date(subset.after).getTime());

            const maxCheck =
                superset.before === undefined ||
                (subset.before !== undefined &&
                    new Date(superset.before).getTime() >=
                        new Date(subset.before).getTime());

            return minCheck && maxCheck;
        });
    }

    return false;
};

export const isConditionIdentical = (
    condition: TransactionRuleCondition,
    ruleToCheck: TransactionRule,
) => {
    const conditionsToCheck = ruleToCheck.conditions.filter(
        (c) => c.conditionType === condition.conditionType,
    );

    // If there are no conditions of the same type, it can't be identical
    if (!conditionsToCheck.length) {
        return false;
    }

    if (
        [RuleConditionType.CATEGORY, RuleConditionType.COUNTERPARTY].includes(
            condition.conditionType,
        )
    ) {
        return conditionsToCheck
            .map((conditionToCheck) => conditionToCheck.value as string)
            .every((value) => value === (condition.value as string));
    } else if (
        [
            RuleConditionType.ENTITY,
            RuleConditionType.FINANCIAL_ACCOUNTS,
            RuleConditionType.DESCRIPTION_IS,
            RuleConditionType.DESCRIPTION_INCLUDES,
        ].includes(condition.conditionType)
    ) {
        // this is not 100% true, but we might decide in the future if we want to allow multiple description conditions
        // it makes sense to allow multiple description conditions, but it is very hard then to evaluate if a rule is a subset of another rule
        // so for now we will go with an optimistic approach
        return conditionsToCheck.every((conditionToCheck) => {
            const arrayValue = Array.isArray(condition.value)
                ? condition.value
                : [condition.value];

            return areArraysEqual(
                arrayValue as string[],
                conditionToCheck.value as string[],
            );
        });
    }
    // there can be multiple amount filters in the past, from now on only single one is allowed,
    // but the current condition must be a subset of every amount filter
    else if (condition.conditionType === RuleConditionType.AMOUNT) {
        return conditionsToCheck.every((conditionToCheck) => {
            const subset = condition.value as RuleConditionAmount;
            const superset = conditionToCheck.value as RuleConditionAmount;

            const minCheck =
                superset.min === undefined ||
                (subset.min !== undefined && superset.min === subset.min);

            const maxCheck =
                superset.max === undefined ||
                (subset.max !== undefined && superset.max === subset.max);

            return minCheck && maxCheck;
        });
    } else if (condition.conditionType === RuleConditionType.DATE) {
        return conditionsToCheck.every((conditionToCheck) => {
            const subset = condition.value as RuleConditionDate;
            const superset = conditionToCheck.value as RuleConditionDate;

            const minCheck =
                superset.after === undefined ||
                (subset.after !== undefined && superset.after === subset.after);

            const maxCheck =
                superset.before === undefined ||
                (subset.before !== undefined &&
                    superset.before === subset.before);

            return minCheck && maxCheck;
        });
    }

    return false;
};

export const isRuleDuplicate = (
    rule: TransactionRule,
    group: TransactionRuleGroup,
) => {
    const currentRuleIndex = group.rules?.findIndex((r) => r.id === rule.id);

    if (currentRuleIndex === undefined || currentRuleIndex <= 0) {
        return false;
    }

    for (let i = 0; i < currentRuleIndex; i++) {
        const ruleToCheck = group.rules?.[i];
        if (!ruleToCheck || ruleToCheck.isDisabled) {
            continue;
        }

        // check all conditions to be a identical of the rule we are checking against
        if (
            rule.conditions.every((condition) =>
                isConditionIdentical(condition, ruleToCheck),
            )
        ) {
            return true;
        }
    }

    return false;
};

export const isRuleUnreachable = (
    rule: TransactionRule,
    group: TransactionRuleGroup,
) => {
    const currentRuleIndex = group.rules?.findIndex((r) => r.id === rule.id);

    if (currentRuleIndex === undefined || currentRuleIndex <= 0) {
        return false;
    }

    for (let i = 0; i < currentRuleIndex; i++) {
        const ruleToCheck = group.rules?.[i];
        if (!ruleToCheck || ruleToCheck.isDisabled) {
            continue;
        }

        // if the previous rule has more conditions, it can't be a subset
        if (ruleToCheck.conditions.length > rule.conditions.length) {
            continue;
        }

        // check all conditions to be a subset of the rule we are checking against
        if (
            rule.conditions.every((condition) =>
                isConditionASubset(condition, ruleToCheck),
            ) &&
            ruleToCheck.conditions.every((condition) =>
                rule.conditions.some(
                    (c) => c.conditionType === condition.conditionType,
                ),
            )
        ) {
            return true;
        }
    }

    return false;
};
